import { client as clientPyObj } from "@rubi/api-generated/clientPy/client.gen";
import { client as clientRsObj } from "@rubi/api-generated/clientRs/client.gen";
import { postSurveyExport } from "@rubi/api-generated/clientRs/sdk.gen";
import { toast } from "@rubi/ui";

import { APP_OR_SERVER_URL, APP_ALERT_SERVER_URL } from "@/environment";

import {
  type ApiRegion,
  API_REGION_STORAGE_KEY,
  baseUrlForRegion,
  DEFAULT_API_REGION,
  isApiRegion,
} from "./region";

// Redirect to /login on 401 responses from clientRs so an expired session
// doesn't silently fail calls mid-workflow (e.g. a reviewer who has been
// moving through reports for long enough that their token has lapsed).
// These answer 401 for bad credentials or a wrong OTP during sign-in. That is
// the call's own result, so it must not be treated as an expired session.
const LOGIN_FLOW_PATHS = [
  "/auth_rs/login",
  "/auth_rs/add_phone",
  "/auth_rs/resend_code",
  "/auth_rs/verify_otp",
];

let sessionExpiredHandled = false;
clientRsObj.instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl: string | undefined = error?.response?.request?.url;
    const configUrl: string | undefined = error?.config?.url;
    const isLoginFlow = LOGIN_FLOW_PATHS.some(
      (path) => requestUrl?.includes(path) || configUrl?.includes(path),
    );
    if (
      !sessionExpiredHandled &&
      window.location.href &&
      !window.location.href.includes("login") &&
      !isLoginFlow &&
      !requestUrl?.includes("user/me") &&
      !requestUrl?.includes("user/me/groups") &&
      error?.response?.status === 401
    ) {
      sessionExpiredHandled = true;
      toast.error(
        "Your session has expired. We are redirecting you to the login page. Please sign in again to continue.",
      );
      // Delay the full-page redirect so the toast is visible before
      // navigation tears down the SnackbarHost.
      setTimeout(() => {
        window.location.href = "/login";
      }, 2500);
    }
    return Promise.reject(error);
  },
);

function storedApiRegion(): ApiRegion | undefined {
  const stored = sessionStorage.getItem(API_REGION_STORAGE_KEY);
  return isApiRegion(stored) ? stored : undefined;
}

// Resolved at module load, before React mounts, so the first request of a full
// page load already goes to the selected deployment. `AuthContext` re-applies
// the stored region too, but only once `userDetails()` has answered — by then
// anything that fetches on mount has already gone out against the default,
// which on a dev-panel reload is the whole page.
let currentRegion: ApiRegion = storedApiRegion() ?? DEFAULT_API_REGION;

export function setApiConfig() {
  if (!APP_OR_SERVER_URL) {
    console.error("setApiConfig: VITE_OR_SERVER_URL is not set");
    return;
  }
  const baseURL = baseUrlForRegion(APP_OR_SERVER_URL, currentRegion);
  clientRsObj.setConfig({
    baseURL,
    withCredentials: true,
    throwOnError: true,
  });
}

/** Point the API client at `apiRegion`'s deployment. */
export function setApiRegion(apiRegion: string) {
  if (!isApiRegion(apiRegion)) {
    console.error(`setApiRegion: '${apiRegion}' is not a valid api_region`);
    return;
  }
  if (apiRegion === currentRegion) {
    return;
  }
  currentRegion = apiRegion;
  setApiConfig();
}

export function getApiRegion(): ApiRegion {
  return currentRegion;
}

export function setApiPyConfig() {
  clientPyObj.setConfig({
    baseURL: APP_ALERT_SERVER_URL,
    withCredentials: true,
    throwOnError: true,
  });
}

setApiConfig();
setApiPyConfig();

export type SurveyExportFormat = "markdown" | "pdf";

/** Export a survey version's rendered summary (markdown or PDF) as a blob. */
export async function exportSurvey(
  versionId: string,
  format: SurveyExportFormat,
): Promise<Blob> {
  const response = await postSurveyExport({
    path: { version_id: versionId },
    headers: { Accept: format === "pdf" ? "application/pdf" : "text/markdown" },
    responseType: "blob",
    throwOnError: true,
  });
  return response.data as Blob;
}

export * as apiReportClient from "./apiReportClient";
export * as apiUserClient from "./apiUserClient";
